/**
 * 収集企業設定サービス（統合版）
 * 本番API、モックAPI、環境切り替えを一つのファイルで管理
 */

import { USE_MOCK_API, API_BASE_URL, IS_DEVELOPMENT } from '../config.js'

// =============================================================================
// モックデータとAPI
// =============================================================================

// モックデータ
let mockCompanies = [
  {
    id: 1,
    companyName: '株式会社清掃サービス',
    companyColor: '#dc3545',
    companyColorName: '赤',
    createdAt: '2024-01-15T09:00:00.000Z',
    updatedAt: '2024-01-15T09:00:00.000Z'
  },
  {
    id: 2,
    companyName: '環境メンテナンス株式会社',
    companyColor: '#fd7e14',
    companyColorName: '橙',
    createdAt: '2024-01-20T10:30:00.000Z',
    updatedAt: '2024-01-20T10:30:00.000Z'
  },
  {
    id: 3,
    companyName: 'クリーンワークス有限会社',
    companyColor: '#ffc107',
    companyColorName: '黄',
    createdAt: '2024-02-01T14:15:00.000Z',
    updatedAt: '2024-02-01T14:15:00.000Z'
  },
  {
    id: 4,
    companyName: 'エコロジー産業株式会社',
    companyColor: '#9ccc65',
    companyColorName: '黄緑',
    createdAt: '2024-02-10T16:45:00.000Z',
    updatedAt: '2024-02-10T16:45:00.000Z'
  },
  {
    id: 5,
    companyName: 'リサイクル推進協会',
    companyColor: '#28a745',
    companyColorName: '緑',
    createdAt: '2024-03-01T11:20:00.000Z',
    updatedAt: '2024-03-01T11:20:00.000Z'
  }
]

// モック用ユーティリティ
const simulateDelay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms))
const shouldSimulateError = () => IS_DEVELOPMENT && Math.random() < 0.1

// 企業色計算ユーティリティ（順番ベース）
const calculateCompanyColor = (companyOrder) => {
  const colors = [
    '#dc3545', // 赤
    '#fd7e14', // 橙
    '#ffc107', // 黄
    '#9ccc65', // 黄緑
    '#28a745', // 緑
    '#007bff', // 青
    '#4a90e2', // 藍
    '#6610f2'  // 紫
  ]

  const colorNames = [
    '赤', '橙', '黄', '黄緑', '緑',
    '青', '藍', '紫'
  ]

  if (!companyOrder || companyOrder < 1) {
    return { color: '#f8f9fa', name: '未設定' }
  }

  const index = (companyOrder - 1) % colors.length

  return {
    color: colors[index],
    name: colorNames[index]
  }
}

// =============================================================================
// モックAPI実装
// =============================================================================

const mockApi = {
  async getCollectionCompanies() {
    await simulateDelay()

    if (shouldSimulateError()) {
      throw new Error('ネットワークエラーが発生しました（モック）')
    }

    return {
      success: true,
      data: [...mockCompanies],
      total: mockCompanies.length,
      timestamp: new Date().toISOString()
    }
  },

  async createCollectionCompany(companyData) {
    await simulateDelay()

    // 重複チェック（企業名）
    const isDuplicateName = mockCompanies.some(company =>
      company.companyName === companyData.companyName
    )

    if (isDuplicateName) {
      throw new Error('この企業名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 企業色を計算（新規追加なので最後の順番）
    const companyOrder = mockCompanies.length + 1
    const colorInfo = calculateCompanyColor(companyOrder)

    // 新しい企業を作成
    const newCompany = {
      id: Math.max(...mockCompanies.map(company => company.id)) + 1,
      companyName: companyData.companyName,
      companyColor: colorInfo.color,
      companyColorName: colorInfo.name,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    mockCompanies.push(newCompany)

    return {
      success: true,
      data: newCompany,
      message: '収集企業が正常に作成されました'
    }
  },

  async updateCollectionCompany(id, companyData) {
    await simulateDelay()

    const companyId = parseInt(id)
    const companyIndex = mockCompanies.findIndex(company => company.id === companyId)

    if (companyIndex === -1) {
      throw new Error('指定された企業が見つかりません')
    }

    // 重複チェック（編集対象以外との企業名重複）
    const isDuplicateName = mockCompanies.some(company =>
      company.id !== companyId && company.companyName === companyData.companyName
    )

    if (isDuplicateName) {
      throw new Error('この企業名は既に登録されています')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 企業色を計算（更新時は現在の順番を維持）
    const companyOrder = companyIndex + 1
    const colorInfo = calculateCompanyColor(companyOrder)

    // 企業を更新
    const updatedCompany = {
      ...mockCompanies[companyIndex],
      companyName: companyData.companyName,
      companyColor: colorInfo.color,
      companyColorName: colorInfo.name,
      updatedAt: new Date().toISOString()
    }

    mockCompanies[companyIndex] = updatedCompany

    return {
      success: true,
      data: updatedCompany,
      message: '収集企業が正常に更新されました'
    }
  },

  async deleteCollectionCompany(id) {
    await simulateDelay()

    const companyId = parseInt(id)
    const companyIndex = mockCompanies.findIndex(company => company.id === companyId)

    if (companyIndex === -1) {
      throw new Error('指定された企業が見つかりません')
    }

    if (shouldSimulateError()) {
      throw new Error('サーバーエラーが発生しました（モック）')
    }

    // 削除制約チェック（例：最後の企業は削除不可）
    if (mockCompanies.length <= 1) {
      throw new Error('最後の企業は削除できません')
    }

    const deletedCompany = mockCompanies.splice(companyIndex, 1)[0]

    return {
      success: true,
      data: deletedCompany,
      message: '収集企業が正常に削除されました'
    }
  }
}

// =============================================================================
// 本番API実装
// =============================================================================

const productionApi = {
  async getCollectionCompanies() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-companies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
          // 'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to fetch collection companies:', error)
      throw new Error('収集企業一覧の取得に失敗しました')
    }
  },

  async createCollectionCompany(companyData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(companyData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この企業名は既に登録されています')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to create collection company:', error)
      if (error.message.includes('既に登録されています')) {
        throw error
      }
      throw new Error('収集企業の作成に失敗しました')
    }
  },

  async updateCollectionCompany(id, companyData) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-companies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        },
        body: JSON.stringify(companyData)
      })

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('この企業名は既に登録されています')
        }
        if (response.status === 404) {
          throw new Error('指定された企業が見つかりません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Failed to update collection company:', error)
      if (error.message.includes('既に登録されています') || error.message.includes('見つかりません')) {
        throw error
      }
      throw new Error('収集企業の更新に失敗しました')
    }
  },

  async deleteCollectionCompany(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/collection-companies/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // TODO: 認証ヘッダーを追加
        }
      })

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('指定された企業が見つかりません')
        }
        if (response.status === 409) {
          throw new Error('この企業は使用中のため削除できません')
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Failed to delete collection company:', error)
      if (error.message.includes('見つかりません') || error.message.includes('削除できません')) {
        throw error
      }
      throw new Error('収集企業の削除に失敗しました')
    }
  }
}

// =============================================================================
// 統合サービスインターフェース
// =============================================================================

// 環境に応じたAPIサービスを選択
const apiService = USE_MOCK_API ? mockApi : productionApi

if (USE_MOCK_API) {
  console.log('🔧 Using mock API for collection companies')
} else {
  console.log('🌐 Using production API for collection companies')
}

/**
 * 収集企業一覧を取得
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const getCollectionCompanies = async () => {
  try {
    const response = await apiService.getCollectionCompanies()

    // レスポンス形式を統一
    if (response.success !== undefined) {
      // モックAPIのレスポンス形式の場合
      return response
    } else {
      // 直接配列が返される場合
      return {
        success: true,
        data: response,
        total: Array.isArray(response) ? response.length : 0
      }
    }
  } catch (error) {
    console.error('Service: Failed to get collection companies:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集企業一覧の取得に失敗しました'
    }
  }
}

/**
 * 新しい収集企業を作成
 * @param {Object} companyData - 企業データ
 * @param {string} companyData.companyName - 企業名（必須）
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const createCollectionCompany = async (companyData) => {
  try {
    // バリデーション
    if (!companyData?.companyName?.trim()) {
      return {
        success: false,
        data: [],
        message: '企業名は必須です'
      }
    }

    if (companyData.companyName.length > 50) {
      return {
        success: false,
        data: [],
        message: '企業名は50文字以内で入力してください'
      }
    }

    const cleanData = {
      companyName: companyData.companyName.trim()
    }

    const response = await apiService.createCollectionCompany(cleanData)

    // 作成後の全データを取得
    const allCompanies = await apiService.getCollectionCompanies()

    return {
      success: true,
      data: allCompanies.data || allCompanies,
      message: response.message || '収集企業が正常に作成されました'
    }
  } catch (error) {
    console.error('Service: Failed to create collection company:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集企業の作成に失敗しました'
    }
  }
}

/**
 * 収集企業を更新
 * @param {string|number} id - 企業ID
 * @param {Object} companyData - 更新する企業データ
 * @param {string} companyData.companyName - 企業名（必須）
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const updateCollectionCompany = async (id, companyData) => {
  try {
    // バリデーション
    if (!id) {
      return {
        success: false,
        data: [],
        message: '企業IDが指定されていません'
      }
    }

    if (!companyData?.companyName?.trim()) {
      return {
        success: false,
        data: [],
        message: '企業名は必須です'
      }
    }

    if (companyData.companyName.length > 50) {
      return {
        success: false,
        data: [],
        message: '企業名は50文字以内で入力してください'
      }
    }

    const cleanData = {
      companyName: companyData.companyName.trim()
    }

    const response = await apiService.updateCollectionCompany(id, cleanData)

    // 更新後の全データを取得
    const allCompanies = await apiService.getCollectionCompanies()

    return {
      success: true,
      data: allCompanies.data || allCompanies,
      message: response.message || '収集企業が正常に更新されました'
    }
  } catch (error) {
    console.error('Service: Failed to update collection company:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集企業の更新に失敗しました'
    }
  }
}

/**
 * 収集企業を削除
 * @param {string|number} id - 企業ID
 * @returns {Promise<Object>} レスポンスオブジェクト {success: boolean, data: Array}
 */
export const deleteCollectionCompany = async (id) => {
  try {
    // バリデーション
    if (!id) {
      return {
        success: false,
        data: [],
        message: '企業IDが指定されていません'
      }
    }

    const response = await apiService.deleteCollectionCompany(id)

    // 削除後の全データを取得
    const allCompanies = await apiService.getCollectionCompanies()

    return {
      success: true,
      data: allCompanies.data || allCompanies,
      message: response.message || '収集企業が正常に削除されました'
    }
  } catch (error) {
    console.error('Service: Failed to delete collection company:', error)
    return {
      success: false,
      data: [],
      message: error.message || '収集企業の削除に失敗しました'
    }
  }
}

/**
 * 開発用：モックデータをリセット
 * 本番環境では何もしない
 */
export const resetMockData = () => {
  if (USE_MOCK_API) {
    mockCompanies = [
      {
        id: 1,
        companyName: '株式会社清掃サービス',
        companyColor: '#dc3545',
        companyColorName: '赤',
        createdAt: '2024-01-15T09:00:00.000Z',
        updatedAt: '2024-01-15T09:00:00.000Z'
      },
      {
        id: 2,
        companyName: '環境メンテナンス株式会社',
        companyColor: '#fd7e14',
        companyColorName: '橙',
        createdAt: '2024-01-20T10:30:00.000Z',
        updatedAt: '2024-01-20T10:30:00.000Z'
      },
      {
        id: 3,
        companyName: 'クリーンワークス有限会社',
        companyColor: '#ffc107',
        companyColorName: '黄',
        createdAt: '2024-02-01T14:15:00.000Z',
        updatedAt: '2024-02-01T14:15:00.000Z'
      },
      {
        id: 4,
        companyName: 'エコロジー産業株式会社',
        companyColor: '#9ccc65',
        companyColorName: '黄緑',
        createdAt: '2024-02-10T16:45:00.000Z',
        updatedAt: '2024-02-10T16:45:00.000Z'
      },
      {
        id: 5,
        companyName: 'リサイクル推進協会',
        companyColor: '#28a745',
        companyColorName: '緑',
        createdAt: '2024-03-01T11:20:00.000Z',
        updatedAt: '2024-03-01T11:20:00.000Z'
      }
    ]
    console.log('Collection company mock data has been reset')
  }
}

/**
 * 現在使用中のAPIタイプを取得
 * @returns {string} 'mock' または 'production'
 */
export const getApiType = () => {
  return USE_MOCK_API ? 'mock' : 'production'
}

/**
 * 企業色計算関数をエクスポート（View層から使用）
 */
export { calculateCompanyColor }

export default {
  getCollectionCompanies,
  createCollectionCompany,
  updateCollectionCompany,
  deleteCollectionCompany,
  resetMockData,
  getApiType,
  calculateCompanyColor
}
